// FloatingInspector.js - Floating Component Inspector Panel

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

const FloatingInspector = () => {
  const {
    isInspectorOpen,
    isInspectorPinned,
    inspectorPosition,
    selectedNodeId,
    nodes,
    calculationResults,
    sweepResults,
    // openEditModal, // Removed - using Component Details Panel instead
    closeInspector,
    toggleInspectorPin,
    setInspectorPosition
  } = useStore();

  // Draggable state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const nodeResults = selectedNodeId ? calculationResults[selectedNodeId] : null;

  // Mouse event handlers for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !panelRef.current) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const newX = inspectorPosition.x + deltaX;
      const newY = inspectorPosition.y + deltaY;

      // Boundary constraints - keep at least 50px visible
      const minVisible = 50;
      const rect = panelRef.current.getBoundingClientRect();

      const constrainedX = Math.max(
        minVisible - rect.width,
        Math.min(newX, window.innerWidth - minVisible)
      );
      const constrainedY = Math.max(
        minVisible,
        Math.min(newY, window.innerHeight - minVisible)
      );

      setInspectorPosition({ x: constrainedX, y: constrainedY });
      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, inspectorPosition, setInspectorPosition]);

  if (!isInspectorOpen) return null;

  const handleMouseDown = (e) => {
    // Only start drag if clicking on header (not buttons)
    if (e.target.closest('button')) return;

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleClose = () => {
    closeInspector();
  };

  const handleTogglePin = () => {
    toggleInspectorPin();
  };

  // Render sweep results
  if (sweepResults) {
    return (
      <Container ref={panelRef} $position={inspectorPosition}>
        <Header onMouseDown={handleMouseDown}>
          <Title>Frequency Sweep Results</Title>
          <HeaderButtons>
            <IconButton onClick={handleClose} title="Close">
              ✕
            </IconButton>
          </HeaderButtons>
        </Header>

        <Content>
          <Section>
            <SectionTitle>Sweep Parameters</SectionTitle>
            <InfoRow>
              <Label>Frequency Range:</Label>
              <Value>{sweepResults.frequencies[0].toFixed(1)} - {sweepResults.frequencies[sweepResults.frequencies.length - 1].toFixed(1)} MHz</Value>
            </InfoRow>
            <InfoRow>
              <Label>Points:</Label>
              <Value>{sweepResults.frequencies.length}</Value>
            </InfoRow>
          </Section>

          <Section>
            <SectionTitle>Results Summary</SectionTitle>
            <InfoRow>
              <Label>Gain (Avg):</Label>
              <Value>{(sweepResults.gain.reduce((a, b) => a + b, 0) / sweepResults.gain.length).toFixed(2)} dB</Value>
            </InfoRow>
            <InfoRow>
              <Label>NF (Avg):</Label>
              <Value>{(sweepResults.noiseFigure.reduce((a, b) => a + b, 0) / sweepResults.noiseFigure.length).toFixed(2)} dB</Value>
            </InfoRow>
          </Section>

          <Button onClick={() => useStore.getState().setSweepResults(null)}>
            Close Sweep
          </Button>
        </Content>
      </Container>
    );
  }

  // Render empty state
  if (!selectedNode) {
    return (
      <Container ref={panelRef} $position={inspectorPosition}>
        <Header onMouseDown={handleMouseDown}>
          <Title>Component Details</Title>
          <HeaderButtons>
            <IconButton
              onClick={handleTogglePin}
              title={isInspectorPinned ? "Unpin (auto-hide)" : "Pin (stay open)"}
              $active={isInspectorPinned}
            >
              📌
            </IconButton>
            <IconButton onClick={handleClose} title="Close">
              ✕
            </IconButton>
          </HeaderButtons>
        </Header>

        <Content>
          <EmptyMessage>Select a component to view details</EmptyMessage>
        </Content>
      </Container>
    );
  }

  // Render component details
  return (
    <Container ref={panelRef} $position={inspectorPosition}>
      <Header onMouseDown={handleMouseDown}>
        <Title>Component Details</Title>
        <HeaderButtons>
          <IconButton
            onClick={handleTogglePin}
            title={isInspectorPinned ? "Unpin (auto-hide)" : "Pin (stay open)"}
            $active={isInspectorPinned}
          >
            📌
          </IconButton>
          <IconButton onClick={handleClose} title="Close">
            ✕
          </IconButton>
        </HeaderButtons>
      </Header>

      <Content>
        <Section>
          <SectionTitle>{selectedNode.data.name || selectedNode.type}</SectionTitle>
          <InfoRow>
            <Label>Type:</Label>
            <Value>{selectedNode.type}</Value>
          </InfoRow>
          <InfoRow>
            <Label>ID:</Label>
            <Value style={{ fontSize: '10px' }}>{selectedNode.id}</Value>
          </InfoRow>
        </Section>

        <Section>
          <SectionTitle>Parameters</SectionTitle>
          {Object.entries(selectedNode.data.parameters || {}).map(([key, param]) => (
            param.displayOnCard && (
              <InfoRow key={key}>
                <Label>{param.name}:</Label>
                <Value>{param.value} {param.unit}</Value>
              </InfoRow>
            )
          ))}
          {/* Edit Parameters button removed - use Component Details Panel (D key or floating button) */}
        </Section>

        {nodeResults && (
          <Section>
            <SectionTitle>Calculated Results</SectionTitle>
            <InfoRow>
              <Label>Input Power:</Label>
              <Value>{nodeResults.inputPower} dBm</Value>
            </InfoRow>
            <InfoRow>
              <Label>Output Power:</Label>
              <Value>{nodeResults.outputPower} dBm</Value>
            </InfoRow>
            <InfoRow>
              <Label>Gain:</Label>
              <Value>{nodeResults.gain} dB</Value>
            </InfoRow>
            <InfoRow>
              <Label>Cumulative Gain:</Label>
              <Value>{nodeResults.cumulativeGain} dB</Value>
            </InfoRow>
            <InfoRow>
              <Label>Cumulative NF:</Label>
              <Value>{nodeResults.cumulativeNF} dB</Value>
            </InfoRow>
            {nodeResults.compression !== '0.000' && (
              <InfoRow>
                <Label>Compression:</Label>
                <Value style={{ color: THEME.warning }}>
                  {nodeResults.compression} dB
                </Value>
              </InfoRow>
            )}
            {nodeResults.iip3 !== 'N/A' && (
              <InfoRow>
                <Label>IIP3:</Label>
                <Value>{nodeResults.iip3} dBm</Value>
              </InfoRow>
            )}
            {nodeResults.oip3 !== 'N/A' && (
              <InfoRow>
                <Label>OIP3:</Label>
                <Value>{nodeResults.oip3} dBm</Value>
              </InfoRow>
            )}
          </Section>
        )}

        {calculationResults['path_0'] && (
          <Section>
            <SectionTitle>System Summary</SectionTitle>
            <InfoRow>
              <Label>Total Gain:</Label>
              <Value>{calculationResults['path_0'].totalGain} dB</Value>
            </InfoRow>
            <InfoRow>
              <Label>System NF:</Label>
              <Value>{calculationResults['path_0'].noiseFigure} dB</Value>
            </InfoRow>
            <InfoRow>
              <Label>Output Power:</Label>
              <Value>{calculationResults['path_0'].outputPower} dBm</Value>
            </InfoRow>
            {calculationResults['path_0'].oip3 !== 'N/A' && (
              <InfoRow>
                <Label>System OIP3:</Label>
                <Value>{calculationResults['path_0'].oip3} dBm</Value>
              </InfoRow>
            )}
          </Section>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  top: ${props => props.$position?.y || 80}px;
  left: ${props => props.$position?.x || window.innerWidth - 370}px;
  width: 320px;
  max-height: 80vh;
  background: rgba(42, 42, 42, 0.98);
  border: 1px solid #404040;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  z-index: 900;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${THEME.spacing.md} ${THEME.spacing.lg};
  border-bottom: 1px solid #404040;
  cursor: move;
  user-select: none;
  background: rgba(50, 50, 50, 0.5);
  border-radius: 8px 8px 0 0;

  &:active {
    cursor: grabbing;
  }
`;

const Title = styled.h2`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.lg};
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: ${THEME.spacing.sm};
`;

const IconButton = styled.button`
  background: ${props => props.$active ? THEME.buttonPrimary : 'transparent'};
  color: ${props => props.$active ? THEME.textPrimary : THEME.textSecondary};
  padding: ${THEME.spacing.xs} ${THEME.spacing.sm};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.md};
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: ${THEME.buttonSecondary};
    color: ${THEME.textPrimary};
  }
`;

const Content = styled.div`
  overflow-y: auto;
  padding: ${THEME.spacing.lg};
  flex: 1;
`;

const Section = styled.div`
  margin-bottom: ${THEME.spacing.lg};
  padding-bottom: ${THEME.spacing.lg};
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  margin-bottom: ${THEME.spacing.md};
  font-weight: 600;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${THEME.spacing.sm};
`;

const Label = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.sm};
`;

const Value = styled.span`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.sm};
  font-weight: 600;
`;

const EmptyMessage = styled.div`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.md};
  text-align: center;
  margin-top: ${THEME.spacing.xxl};
`;

const Button = styled.button`
  width: 100%;
  background: ${THEME.buttonPrimary};
  color: ${THEME.textPrimary};
  padding: ${THEME.spacing.sm};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};
  margin-top: ${THEME.spacing.md};
  transition: background ${THEME.transitionFast};

  &:hover {
    background: ${THEME.buttonHover};
  }
`;

export default FloatingInspector;
